'use client';
import { useCallback, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ViewStateChangeEvent } from 'react-map-gl';
import { IconMapPin } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { countryAndStates } from 'piramid-constants';
import { cn } from 'ui';
import { toTitleCase } from 'utils';

const TWENTY_DAYS_MS = 20 * 24 * 60 * 60 * 1000;

const MAPBOX_ACCESS_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN || '';

// mapbox-gl (~400 kB gzip) is only downloaded once the user opens the interactive map.
const SinisterInteractiveMap = dynamic(
  () =>
    import('./SinisterInteractiveMap').then(
      (module) => module.SinisterInteractiveMap,
    ),
  { ssr: false },
);

interface LocationData {
  country?: string | null;
  state?: string | null;
  city?: string | null;
  address?: string;
  zip_code?: string | null;
}

interface SinisterLocationMapProps {
  locationData: LocationData;
  className?: string;
  height?: string;
  country?: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface ViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

export const SinisterLocationMap = ({
  locationData,
  className,
  height = '20rem',
  country = 'ARG',
  latitude: backendLatitude,
  longitude: backendLongitude,
}: SinisterLocationMapProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showInteractiveMap, setShowInteractiveMap] = useState(false);
  const [manualViewState, setManualViewState] = useState<ViewState | null>(
    null,
  );

  const hasBackendCoordinates =
    backendLatitude != null && backendLongitude != null;

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(node);
  }, []);

  const buildSearchQuery = useCallback(
    (data: LocationData): string => {
      const countryName =
        countryAndStates.find((c) => c.code === country)?.name || country;
      const stateName =
        countryAndStates
          .find((c) => c.code === country)
          ?.states.find((s) => s.code === data.state)?.name || data.state;

      return [
        data.address?.trim(),
        data.city?.trim(),
        stateName?.trim(),
        countryName?.trim(),
      ]
        .filter((segment) => !!segment)
        .join(', ');
    },
    [country],
  );

  const getCoordinates = useCallback(
    async (
      query: string,
      isRetry = false,
    ): Promise<[number, number] | null> => {
      if (!query.trim()) {
        throw new Error('No hay datos de ubicación disponibles');
      }

      try {
        const baseUrl = process.env.NEXT_PUBLIC_MAPBOX_GEOCODING_URL;
        const params = new URLSearchParams({
          access_token: MAPBOX_ACCESS_TOKEN,
          country: country,
          limit: '1',
          types: isRetry ? 'place,locality' : 'address,poi,place',
        });

        const url = `${baseUrl}${encodeURIComponent(query)}.json?${params}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Error al obtener coordenadas');
        }

        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          const [lng, lat] = feature.center;

          return [lng, lat];
        }

        if (!isRetry && query.includes(',')) {
          const simplifiedQuery = query.split(',').slice(0, -1).join(',');
          return getCoordinates(simplifiedQuery, true);
        }

        throw new Error('No se pudo encontrar la ubicación');
      } catch (err) {
        console.error('Error al geocodificar:', err);
        throw err;
      }
    },
    [country],
  );

  const searchQuery = buildSearchQuery(locationData);

  // Only fetch coordinates from Mapbox if backend didn't provide them
  const {
    data: geocodedCoordinates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['mapbox-geocoding', searchQuery],
    queryFn: () => getCoordinates(searchQuery),
    enabled: isVisible && !hasBackendCoordinates && !!searchQuery.trim(),
    staleTime: Infinity,
    gcTime: TWENTY_DAYS_MS,
    retry: false,
  });

  // Resolve final coordinates: backend first, then geocoded fallback
  const coordinates: [number, number] | null = useMemo(() => {
    if (hasBackendCoordinates) {
      return [backendLongitude, backendLatitude];
    }
    return geocodedCoordinates ?? null;
  }, [
    hasBackendCoordinates,
    backendLongitude,
    backendLatitude,
    geocodedCoordinates,
  ]);

  const viewState = useMemo(() => {
    if (manualViewState) {
      return manualViewState;
    }

    if (coordinates) {
      const [lng, lat] = coordinates;
      return {
        longitude: lng,
        latitude: lat,
        zoom: 15,
      };
    }

    return null;
  }, [coordinates, manualViewState]);

  const handleMapMove = useCallback((evt: ViewStateChangeEvent) => {
    setManualViewState(evt.viewState);
  }, []);

  const handleStaticMapClick = useCallback(() => {
    setShowInteractiveMap(true);
  }, []);

  const staticMapUrl = useMemo(() => {
    if (!coordinates) return null;
    const [lng, lat] = coordinates;
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+FC762D(${lng},${lat})/${lng},${lat},15,0/600x400@2x?access_token=${MAPBOX_ACCESS_TOKEN}`;
  }, [coordinates]);

  if (!isVisible) {
    return (
      <div
        ref={containerRef}
        className={cn(
          'rounded-md border bg-muted flex items-center justify-center',
          className,
        )}
        style={{ height }}
      >
        <IconMapPin className='h-8 w-8 text-muted-foreground' />
      </div>
    );
  }

  if (error && !hasBackendCoordinates) {
    return (
      <div
        className={cn(
          'rounded-md border bg-card p-4 flex flex-col items-center justify-center text-center',
          className,
        )}
        style={{ height }}
      >
        <IconMapPin className='h-8 w-8 text-muted-foreground mb-2' />
        <p className='text-sm text-muted-foreground'>
          {error instanceof Error ? error.message : 'Error al cargar el mapa'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn('relative rounded-md overflow-hidden border', className)}
      style={{ height }}
    >
      {isLoading && !hasBackendCoordinates && (
        <div className='absolute inset-0 bg-muted flex items-center justify-center z-10'>
          <div className='flex flex-col items-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
            <p className='text-sm text-muted-foreground mt-2'>
              Cargando mapa...
            </p>
          </div>
        </div>
      )}

      {staticMapUrl && !showInteractiveMap && (
        <button
          type='button'
          onClick={handleStaticMapClick}
          className='w-full h-full cursor-pointer border-0 p-0 bg-transparent'
          aria-label='Haz clic para interactuar con el mapa'
        >
          <img
            src={staticMapUrl}
            alt={`Mapa de ubicación: ${toTitleCase(searchQuery)}`}
            className='w-full h-full object-cover'
          />
        </button>
      )}

      {showInteractiveMap && viewState && coordinates && (
        <SinisterInteractiveMap
          viewState={viewState}
          coordinates={coordinates}
          label={toTitleCase(searchQuery)}
          accessToken={MAPBOX_ACCESS_TOKEN}
          onMove={handleMapMove}
        />
      )}
    </div>
  );
};
